// src/components/ContactUs.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/Contact.css';

const Contact = () => {
  const location = useLocation();
  const prefill = location.state?.contact || null;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    concern: ''
  });
  const [loading, setLoading] = useState(false);
  const [formStatus, setFormStatus] = useState(null); // {type: 'success'|'error', text: '...'}

  const messageRef = useRef(null);
  const appliedPrefillRef = useRef(false);

  // map concern to recipient
  const emailMap = {
    general: 'finance@afeworkinternational.com',
    medical: 'medical@afeworkinternational.com',
    other: 'manager@afeworkinternational.com'
  };

  // Apply prefill only once and only if user hasn't started typing
  useEffect(() => {
    if (!prefill || appliedPrefillRef.current) return;

    const isEmpty = !formData.name && !formData.email && !formData.message && !formData.concern;

    if (isEmpty) {
      setFormData(prev => ({
        ...prev,
        concern: prefill.concern || prev.concern,
        message: prefill.message || prev.message,
        name: prefill.name || prev.name,
        email: prefill.email || prev.email
      }));

      // Focus the message textarea after a tiny delay for UX
      setTimeout(() => {
        if (messageRef.current) messageRef.current.focus();
      }, 80);
    }

    appliedPrefillRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', message: '', concern: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus(null);

    // basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim() || !formData.concern) {
      setFormStatus({ type: 'error', text: 'Please fill in all fields.' });
      return;
    }

    setLoading(true);

    const recipient = emailMap[formData.concern] || emailMap.general;

    const fd = new FormData();
    fd.append('name', formData.name);
    fd.append('email', formData.email);
    fd.append('message', formData.message);
    fd.append('concern', formData.concern);
    fd.append('_subject', `Website contact — ${formData.concern || 'general'}`);
    fd.append('_captcha', 'false');

    try {
      const res = await fetch(`https://formsubmit.co/${recipient}`, {
        method: 'POST',
        body: fd
      });

      if (res.ok) {
        setFormStatus({ type: 'success', text: 'Thanks — your message was sent. We will contact you shortly.' });
        resetForm();
      } else {
        setFormStatus({ type: 'error', text: 'Sorry — failed to send your message. Please try again later.' });
      }
    } catch (err) {
      console.error('Contact submit error', err);
      setFormStatus({ type: 'error', text: 'Network error. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contacts-section" id="contact" aria-labelledby="contact-heading">
      <div className="contacts-inner container-grid">
        {/* LEFT: Contact Info */}
        <aside className="contact-info">
          <h2 id="contact-heading" className="contact-title">Contact Us</h2>

          <p className="contact-lead">
            We're here to help — whether you need product information, a quote, or after-sales support.
            Choose the relevant topic and send us a message. We'll respond promptly.
          </p>

          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon">📍</div>
              <div>
                <div className="info-label">Office</div>
                <div className="info-text"><a href="https://www.google.com/maps/place/2Q5C+WJX,+Addis+Ababa" target="_blank" rel="noreferrer">2Q5C+WJX, Addis Ababa</a></div>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">📞</div>
              <div>
                <div className="info-label">Phone</div>
                <div className="info-text"><a href="tel:+251115577723">+251 11 557 7723</a></div>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">✉️</div>
              <div>
                <div className="info-label">Email</div>
                <div className="info-text"><a href="mailto:info@afeworkinternational.com">info@afeworkinternational.com</a></div>
              </div>
            </div>
          </div>

          <div className="hours-card">
            <div className="hours-title">Opening hours</div>
            <div className="hours-list">
              <div>Mon — Fri: 08:30 — 17:30</div>
              <div>Sat: 09:00 — 13:00</div>
              <div>Sun: Closed</div>
            </div>
          </div>
        </aside>

        {/* RIGHT: Form */}
        <div className="contact-form-card" role="form" aria-label="Contact form">
          <form onSubmit={handleSubmit} className="contact-form" noValidate>
            <label className="field">
              <span className="field-label">Your name</span>
              <input
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full name"
                required
                aria-required="true"
                aria-label="Your name"
              />
            </label>

            <label className="field">
              <span className="field-label">Email</span>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                aria-required="true"
                aria-label="Your email"
              />
            </label>

            <label className="field">
              <span className="field-label">Topic</span>
              <select
                name="concern"
                value={formData.concern}
                onChange={handleChange}
                required
                aria-required="true"
                aria-label="Select concern"
              >
                <option value="">Select a topic</option>
                <option value="general">General inquiry</option>
                <option value="medical">Medical procurement</option>
                <option value="other">Other / Management</option>
              </select>
            </label>

            <label className="field">
              <span className="field-label">Message</span>
              <textarea
                name="message"
                ref={messageRef}
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your needs or question..."
                required
                aria-required="true"
                rows="6"
              />
            </label>

            <div className="form-actions">
              <button
                type="submit"
                className={`btn btn-submit ${loading ? 'loading' : ''}`}
                disabled={loading}
                aria-disabled={loading}
              >
                {loading ? <span className="btn-spinner" aria-hidden /> : 'Send message'}
              </button>
            </div>

            {formStatus && (
              <div
                className={`form-status ${formStatus.type === 'success' ? 'success' : 'error'}`}
                role={formStatus.type === 'success' ? 'status' : 'alert'}
              >
                {formStatus.text}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
